'use client';

import { useRef } from 'react';
import { Box, Button, ButtonGroup, IconButton } from '@zillow/constellation-10';
import { Share } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { LucideIcon } from '@/src/components/LucideIcon';
import { useListingActions } from '@/src/components/features/listing-actions';
import { SHARE_CLICKSTREAM_CONTEXTS } from '@/src/components/features/listing-actions/listingActionUtils';
import { ListingActionCluster } from '@/src/components/features/listings/ListingActionCluster';
import { ListingActionMenu } from '@/src/components/features/listings/ListingActionMenu';
import { FavoriteHeartIcon } from '@/src/components/features/listings/FavoriteHeartIcon';
import { useListingCTAs, useListingSaveActions } from '@/src/components/features/property/hooks';
import type { PropertyDetailListing } from '@/src/components/features/property/types';
import { useShareAction } from '@/src/lib/hooks/useShareAction';
import type { ExtendedListing } from '@/src/lib/api/endpoints/listing';
import * as styles from '@/src/components/MediaModal/mediaModal.styles';

type Props = {
  actionSet?: 'all' | 'primary' | 'secondary';
  listing: PropertyDetailListing;
  triggerLocation: 'home_details_media_gallery' | 'home_details_photo_lightbox';
  onImpact?: boolean;
  presentation: 'desktop' | 'mobile';
  primaryOrientation?: 'horizontal' | 'vertical';
};

export function MediaModalActions({
  actionSet = 'all',
  listing,
  triggerLocation,
  onImpact = false,
  presentation,
  primaryOrientation = 'horizontal',
}: Props) {
  const t = useTranslations('hdp');
  const analyticsContext =
    triggerLocation === 'home_details_media_gallery'
      ? SHARE_CLICKSTREAM_CONTEXTS.mediaGallery
      : SHARE_CLICKSTREAM_CONTEXTS.photoLightbox;
  const favoriteButtonRef = useRef<HTMLButtonElement>(null);
  const { listingSaved, toggleSave, identityResolved } = useListingSaveActions(
    listing,
    favoriteButtonRef,
    analyticsContext,
  );
  const { ctas, overlays } = useListingCTAs(listing, analyticsContext.triggerLocation, analyticsContext.triggerObject, {
    triggerLocation,
    triggerObject: 'home_details_component|summary',
  });
  const { openShare, shareFormOverlay } = useShareAction(listing as ExtendedListing, analyticsContext);
  const { listingHidden, toggleHide, openReport } = useListingActions(listing, analyticsContext);
  const tone = onImpact ? 'neutral-fixed' : 'neutral';
  const secondaryEmphasis = onImpact ? 'filled' : 'bare';
  const secondaryActionTone = presentation === 'mobile' ? 'neutral' : tone;
  const secondaryActionEmphasis = presentation === 'mobile' ? 'bare' : secondaryEmphasis;
  const secondaryActionOnImpact = presentation === 'desktop' && onImpact;
  const secondaryActionClass = secondaryActionOnImpact ? styles.mediaActionSecondaryOnImpact : undefined;
  const saveAriaLabel = listingSaved ? t('removeFromSaved') : t('saveThisListing');
  const moveMobileSecondaryActionsToHeader = presentation === 'mobile';
  const showPrimaryActions = actionSet !== 'secondary';
  const showSecondaryActions = actionSet !== 'primary';
  const orderedCtas = presentation === 'mobile' ? [...ctas].reverse() : ctas;
  const listingActionMenu = (
    <ListingActionMenu
      listingHidden={listingHidden}
      onHide={toggleHide}
      onReport={openReport}
      tone={secondaryActionTone}
      emphasis={secondaryActionEmphasis}
      size="sm"
      onImpact={secondaryActionOnImpact}
      className={secondaryActionClass}
    />
  );
  const mobileSecondaryActions = (
    <>
      <IconButton
        ref={favoriteButtonRef}
        type="button"
        shape="circle"
        tone={secondaryActionTone}
        emphasis={secondaryActionEmphasis}
        onImpact={secondaryActionOnImpact}
        className={secondaryActionClass}
        icon={<FavoriteHeartIcon selected={listingSaved} />}
        title={t('save')}
        onClick={toggleSave}
        disabled={!identityResolved}
        aria-busy={!identityResolved}
        aria-pressed={listingSaved}
        aria-label={saveAriaLabel}
      />
      <IconButton
        type="button"
        shape="circle"
        tone={secondaryActionTone}
        emphasis={secondaryActionEmphasis}
        onImpact={secondaryActionOnImpact}
        className={secondaryActionClass}
        icon={<LucideIcon icon={Share} size="sm" />}
        title={t('share')}
        onClick={openShare}
        aria-label={t('shareThisListing')}
      />
      {listingActionMenu}
    </>
  );
  const showPrimaryGroup =
    showPrimaryActions && (ctas.length > 0 || (presentation === 'desktop' && showSecondaryActions));
  const primaryActions = showPrimaryGroup ? (
    <ButtonGroup
      role="group"
      aria-label={t('listingActions')}
      orientation={primaryOrientation}
      size={presentation === 'mobile' ? 'md' : 'sm'}
      css={primaryOrientation === 'vertical' ? styles.mediaVerticalActionGroup : styles.mediaActionGroup}
    >
      {orderedCtas.map((cta) => (
        <Button
          key={cta.id}
          type="button"
          fluid={presentation === 'mobile'}
          tone="brand"
          emphasis={cta.emphasis}
          icon={presentation === 'desktop' ? cta.icon : undefined}
          data-media-primary-action={presentation === 'mobile' ? '' : undefined}
          onClick={cta.onClick}
          disabled={cta.disabled}
          aria-busy={cta.ariaBusy}
          aria-label={cta.ariaLabel}
        >
          {cta.label}
        </Button>
      ))}

      {presentation === 'desktop' && showSecondaryActions ? (
        <>
          <Button
            ref={favoriteButtonRef}
            type="button"
            tone={tone}
            emphasis={secondaryEmphasis}
            onImpact={onImpact}
            className={onImpact ? styles.mediaActionSecondaryOnImpact : undefined}
            icon={<FavoriteHeartIcon selected={listingSaved} />}
            iconPosition="end"
            onClick={toggleSave}
            disabled={!identityResolved}
            aria-busy={!identityResolved}
            aria-pressed={listingSaved}
            aria-label={saveAriaLabel}
          >
            {t('save')}
          </Button>
          <Button
            type="button"
            tone={tone}
            emphasis={secondaryEmphasis}
            onImpact={onImpact}
            className={onImpact ? styles.mediaActionSecondaryOnImpact : undefined}
            icon={<LucideIcon icon={Share} size="sm" />}
            iconPosition="end"
            onClick={openShare}
            aria-label={t('shareThisListing')}
          >
            {t('share')}
          </Button>
        </>
      ) : presentation === 'mobile' && !moveMobileSecondaryActionsToHeader && showSecondaryActions ? (
        <ButtonGroup role="group" aria-label={t('moreActions')} size="sm">
          {mobileSecondaryActions}
        </ButtonGroup>
      ) : null}
      {presentation === 'desktop' && showSecondaryActions ? listingActionMenu : null}
    </ButtonGroup>
  ) : null;

  return (
    <>
      {primaryActions}
      {presentation === 'desktop' && actionSet === 'secondary' ? (
        <ListingActionCluster label={t('moreActions')}>{mobileSecondaryActions}</ListingActionCluster>
      ) : null}
      {moveMobileSecondaryActionsToHeader && showSecondaryActions ? (
        <Box className={styles.mediaMobileHeaderActions}>
          <ListingActionCluster label={t('moreActions')}>{mobileSecondaryActions}</ListingActionCluster>
        </Box>
      ) : null}
      {showPrimaryActions ? overlays : null}
      {showSecondaryActions ? shareFormOverlay : null}
    </>
  );
}
